import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Account } from '@yada/types';
import { setAccount as setAccountStorage, getSessionToken, getAccount as getAccountStorage } from '@yada/storage';
import { getAccessToken } from '@yada/api-client';
import { updateAccount as updateAccountOnServer } from '@yada/api-client';

interface AccountContextValue {
  account: Account | null;
  /** Called by AuthGuard after boot sync to populate the context */
  initAccount: (account: Account) => void;
  /** Persists enabledApps to localStorage and syncs to server */
  setEnabledApps: (apps: string[]) => Promise<void>;
  /** Persists pinnedApps to localStorage and syncs to server */
  setPinnedApps: (apps: string[]) => Promise<void>;
  /** Atomically persists both enabledApps and pinnedApps in a single write */
  setAppPrefs: (enabledApps: string[], pinnedApps: string[]) => Promise<void>;
  /** Marks onboarding complete in localStorage and syncs to server */
  setOnboardingComplete: () => Promise<void>;
}

const AccountContext = createContext<AccountContextValue>({
  account: null,
  initAccount: () => {},
  setEnabledApps: async () => {},
  setPinnedApps: async () => {},
  setAppPrefs: async () => {},
  setOnboardingComplete: async () => {},
});

export const useAccount = () => useContext(AccountContext);

export const AccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [account, setAccountState] = useState<Account | null>(null);

  const initAccount = useCallback((acc: Account) => {
    setAccountState(acc);
  }, []);

  const setEnabledApps = useCallback(async (apps: string[]) => {
    if (!account) return;
    const currentPinned = new Set(account.pinnedApps ?? []);
    const currentEnabled = new Set(account.enabledApps ?? []);
    const newPinned = apps.filter(id => currentPinned.has(id) || !currentEnabled.has(id));
    const updated: Account = { ...account, enabledApps: apps, pinnedApps: newPinned };
    await setAccountStorage(updated);
    setAccountState(updated);
    // Best-effort server sync
    try {
      const token = getAccessToken() || (await getSessionToken());
      if (token) await updateAccountOnServer(token, { enabled_apps: apps, pinned_apps: newPinned });
    } catch (err) {
      console.warn('[AccountContext] Failed to sync enabledApps to server:', err);
    }
  }, [account]);

  const setPinnedApps = useCallback(async (apps: string[]) => {
    if (!account) return;
    const updated: Account = { ...account, pinnedApps: apps };
    await setAccountStorage(updated);
    setAccountState(updated);
    try {
      const token = getAccessToken() || (await getSessionToken());
      if (token) await updateAccountOnServer(token, { pinned_apps: apps });
    } catch (err) {
      console.warn('[AccountContext] Failed to sync pinnedApps to server:', err);
    }
  }, [account]);

  const setAppPrefs = useCallback(async (enabledApps: string[], pinnedApps: string[]) => {
    if (!account) return;
    const updated: Account = { ...account, enabledApps, pinnedApps };
    await setAccountStorage(updated);
    setAccountState(updated);
    try {
      const token = getAccessToken() || (await getSessionToken());
      if (token) await updateAccountOnServer(token, { enabled_apps: enabledApps, pinned_apps: pinnedApps });
    } catch (err) {
      console.warn('[AccountContext] Failed to sync appPrefs to server:', err);
    }
  }, [account]);

  const setOnboardingComplete = useCallback(async () => {
    if (!account) return;
    // Read the latest persisted account so we don't overwrite changes (e.g.
    // enabledApps) that were written in the same render cycle by setEnabledApps.
    const latest = (await getAccountStorage()) ?? account;
    const updated: Account = { ...latest, onboardingComplete: true };
    await setAccountStorage(updated);
    setAccountState(updated);
    // Server sync — best-effort (no token in local-only mode)
    const token = getAccessToken() || (await getSessionToken());
    if (token) {
      try {
        await updateAccountOnServer(token, { onboarding_complete: true });
      } catch (err) {
        console.warn('[AccountContext] Failed to sync onboardingComplete to server:', err);
      }
    }
  }, [account]);

  return (
    <AccountContext.Provider value={{ account, initAccount, setEnabledApps, setPinnedApps, setAppPrefs, setOnboardingComplete }}>
      {children}
    </AccountContext.Provider>
  );
};
